import React, {Fragment, useRef, useState} from 'react';
import Button from "../UI/Button";
import Modal from "../UI/Modal";
import ModalForm from "../UI/ModalForm";
import ModalFormInputRow from "../UI/ModalFormInputRow";
import Box from "../UI/Box";
import {toast} from "react-toastify";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faCircleXmark, faPlus} from "@fortawesome/free-solid-svg-icons";
import {useItemCategories} from "../../hooks/useItemCategories";
import {createItemCategory, deleteItemCategory, updateItemCategory} from "../../api/itemCategoryApiClient";
import {buildChildrenByParent, collectDescendantIds, flattenForSelect} from "../../utility/itemCategoryTree";

const ItemCategoryTreeNode = ({category, byParent, depth, onEdit, onAddChild, onDelete}) => {
    const children = byParent[category.itemCategoryId] || [];

    return (
        <Fragment>
            <li className="mb-1">
                <div className="flex items-center justify-between hover:bg-gray-200 rounded px-2 py-1" style={{paddingLeft : depth * 24}}>
                    <span>{category.name} <span className="text-gray-400 text-sm">(order {category.order})</span></span>
                    <div className="flex space-x-4">
                        <div className="hover:cursor-pointer" title="Add subcategory" onClick={() => onAddChild(category.itemCategoryId)}>
                            <FontAwesomeIcon icon={faPlus} className="text-lg"/>
                        </div>
                        <div className="hover:cursor-pointer" title="Edit" onClick={() => onEdit(category)}>
                            <FontAwesomeIcon icon={faEdit} className="text-lg"/>
                        </div>
                        <div className="hover:cursor-pointer" title="Delete" onClick={() => onDelete(category.itemCategoryId)}>
                            <FontAwesomeIcon icon={faCircleXmark} className="text-lg"/>
                        </div>
                    </div>
                </div>
            </li>
            {children.length > 0 && (
                <ul>
                    {children.map((child) => (
                        <ItemCategoryTreeNode
                            key={child.itemCategoryId}
                            category={child}
                            byParent={byParent}
                            depth={depth + 1}
                            onEdit={onEdit}
                            onAddChild={onAddChild}
                            onDelete={onDelete}
                        />
                    ))}
                </ul>
            )}
        </Fragment>
    );
};

const ItemCategoryList = () => {
    const [submitCount, setSubmitCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [defaultParentId, setDefaultParentId] = useState(null);
    const nameRef = useRef(null);
    const orderRef = useRef(null);
    const parentIdRef = useRef(null);
    const {itemCategories} = useItemCategories(submitCount);

    const byParent = buildChildrenByParent(itemCategories);
    const rootCategories = byParent['root'] || [];
    const disabledIds = isUpdate && editingCategory
        ? [editingCategory.itemCategoryId, ...collectDescendantIds(editingCategory.itemCategoryId, byParent)]
        : [];
    const selectOptions = flattenForSelect(byParent, disabledIds);

    const handleCreateButtonClick = () => {
        setIsUpdate(false);
        setEditingCategory(null);
        setDefaultParentId(null);
        setShowModal(true);
    };

    const handleAddChildClick = (parentId) => {
        setIsUpdate(false);
        setEditingCategory(null);
        setDefaultParentId(parentId);
        setShowModal(true);
    };

    const handleEditClick = (category) => {
        setIsUpdate(true);
        setEditingCategory(category);
        setShowModal(true);
    };

    const handleDeleteClick = (itemCategoryId) => {
        if (!window.confirm('Are you sure you want to delete this category?')) {
            return;
        }

        deleteItemCategory(itemCategoryId)
            .then(() => {
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success('Success!');
            })
            .catch((error) => {
                if (error.code === 1301) {
                    toast.error('Move or delete the items/subcategories in this category first.');
                }
                else {
                    toast.error(error.message);
                }
            });
    };

    const handleCreateSubmit = (event) => {
        event.preventDefault();

        const parentIdValue = parentIdRef.current.value;
        const parentId = parentIdValue === '' ? null : parseInt(parentIdValue, 10);
        const name = nameRef.current.value;
        const order = parseInt(orderRef.current.value, 10) || 1;

        createItemCategory(parentId, name, order)
            .then(() => {
                setShowModal(false);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success('Success!');
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleEditSubmit = (event) => {
        event.preventDefault();

        const parentIdValue = parentIdRef.current.value;
        const parentId = parentIdValue === '' ? null : parseInt(parentIdValue, 10);
        const name = nameRef.current.value;
        const order = parseInt(orderRef.current.value, 10) || 1;

        updateItemCategory(editingCategory.itemCategoryId, parentId, name, order)
            .then(() => {
                setShowModal(false);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success('Success!');
            })
            .catch((error) => {
                if (error.code === 1308) {
                    toast.error("Can't move a category under itself or one of its own subcategories.");
                }
                else {
                    toast.error(error.message);
                }
            });
    };

    return (
        <Fragment>
            <Box title="Item categories">
                <Button title="Create category" handleClick={handleCreateButtonClick}/>
                {rootCategories.length === 0 && (
                    <p className="text-gray-500 mt-4">No categories yet.</p>
                )}
                <ul className="mt-4">
                    {rootCategories.map((category) => (
                        <ItemCategoryTreeNode
                            key={category.itemCategoryId}
                            category={category}
                            byParent={byParent}
                            depth={0}
                            onEdit={handleEditClick}
                            onAddChild={handleAddChildClick}
                            onDelete={handleDeleteClick}
                        />
                    ))}
                </ul>
            </Box>
            {showModal && (
                <Modal>
                    <form onSubmit={isUpdate ? handleEditSubmit : handleCreateSubmit}>
                        <ModalForm
                            title={isUpdate ? 'Update category' : 'Create category'}
                            saveButtonTitle={isUpdate ? 'Update' : 'Create'}
                            handleCancelButtonClick={() => setShowModal(false)}
                        >
                            <ModalFormInputRow title="Parent category">
                                <select
                                    ref={parentIdRef}
                                    defaultValue={isUpdate ? (editingCategory.parentId ?? '') : (defaultParentId ?? '')}
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                >
                                    <option value="">No parent (root category)</option>
                                    {selectOptions.map((option) => (
                                        <option
                                            key={option.itemCategoryId}
                                            value={option.itemCategoryId}
                                            disabled={option.disabled}
                                        >
                                            {'—'.repeat(option.depth)} {option.name}
                                        </option>
                                    ))}
                                </select>
                            </ModalFormInputRow>
                            <ModalFormInputRow title="Name">
                                <input
                                    ref={nameRef}
                                    type="text"
                                    defaultValue={isUpdate ? editingCategory.name : ''}
                                    placeholder="Enter name"
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                />
                            </ModalFormInputRow>
                            <ModalFormInputRow title="Order">
                                <input
                                    ref={orderRef}
                                    type="number"
                                    defaultValue={isUpdate ? editingCategory.order : 1}
                                    placeholder="Enter order"
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                />
                            </ModalFormInputRow>
                        </ModalForm>
                    </form>
                </Modal>
            )}
        </Fragment>
    );
};

export default ItemCategoryList;
